import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { DataTable, type Column } from '@/components/DataTable'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { useAuth } from '@/features/auth/store'
import { listLocations } from '@/features/inventory/api'
import { TerminalFormDialog } from '@/features/pos/TerminalFormDialog'
import {
  TERMINAL_STATUS_LABELS,
  listShifts,
  listTerminals,
  type Terminal,
  type TerminalStatus,
} from '@/features/pos/api'

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const STATUS_TONE: Record<TerminalStatus, string> = {
  active: 'bg-success/10 text-success',
  inactive: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  maintenance: 'bg-warning/10 text-warning',
}

/**
 * Every till, at every store -- add a second counter, rename one, take one out of service.
 *
 * The seed builds exactly one till per store (see `pos.router`'s docstring: a terminal is a
 * physical drawer, so none at the warehouse or the online channel), but a store with two
 * queues at Saturday-lunch rush needs a second one, and this is where that gets added
 * instead of a raw API call.
 */
export function PosTerminals() {
  const can = useAuth((s) => s.can)
  const [locationFilter, setLocationFilter] = useState('')
  const [dialog, setDialog] = useState<{ terminal?: Terminal } | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['pos-terminals', locationFilter],
    queryFn: () => listTerminals(locationFilter || undefined),
    retry: false,
  })

  const locations = useQuery({
    queryKey: ['stock-locations'],
    queryFn: listLocations,
    retry: false,
  })
  const storeOptions = (locations.data?.data ?? []).filter((location) => location.type === 'store')
  const locationName = (id: string) => storeOptions.find((location) => location.id === id)?.name ?? id

  // Who is on each till right now. A terminal only ever has one open shift at a time (the
  // service enforces that), so this is a 1:1 lookup, not a list -- see `open_shift_id` on
  // `Terminal` for the same fact from the terminal's side.
  const openShifts = useQuery({
    queryKey: ['pos-shifts', 'open'],
    queryFn: () => listShifts({ status: 'open' }),
    retry: false,
  })
  const workingOn: Record<string, string> = {}
  for (const shift of openShifts.data?.data ?? []) {
    workingOn[shift.terminal_id] = shift.opened_by_name ?? 'Unknown'
  }

  const canCreate = can('pos', 'create')
  const canUpdate = can('pos', 'update')

  const rows = data ?? []

  const columns: Column<Terminal>[] = [
    {
      key: 'code',
      header: 'Code',
      className: 'font-medium whitespace-nowrap',
      cell: (row) =>
        canUpdate ? (
          <button
            type="button"
            onClick={() => setDialog({ terminal: row })}
            className="text-left hover:underline"
          >
            {row.code}
          </button>
        ) : (
          row.code
        ),
    },
    { key: 'name', header: 'Name', cell: (row) => row.name },
    {
      key: 'location_id',
      header: 'Store',
      className: 'text-slate-500 dark:text-slate-400',
      cell: (row) => locationName(row.location_id),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => (
        <span className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_TONE[row.status]}`}>
          {TERMINAL_STATUS_LABELS[row.status]}
        </span>
      ),
    },
    {
      key: 'open_shift_id',
      header: 'Managed by right now',
      cell: (row) =>
        row.open_shift_id ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden />
            {workingOn[row.id] ?? 'Someone'}
          </span>
        ) : (
          <span className="text-slate-500 dark:text-slate-400">Unattended</span>
        ),
    },
    {
      key: 'notes',
      header: 'Notes',
      className: 'text-slate-500 dark:text-slate-400',
      cell: (row) => row.notes ?? '—',
    },
  ]

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Tills</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {data ? `${rows.length} till${rows.length === 1 ? '' : 's'}` : 'Loading…'}
          </p>
        </div>
        {canCreate && <Button onClick={() => setDialog({})}>New till</Button>}
      </header>

      <Card>
        <CardHeader title="Tills by store" />
        <DataTable
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          caption="Tills"
          isLoading={isLoading}
          error={error}
          emptyMessage="No tills match this filter."
          toolbar={
            <div className="flex flex-wrap items-center gap-2">
              <label className="sr-only" htmlFor="terminals-location">
                Store
              </label>
              <select
                id="terminals-location"
                className={SELECT_CLASS}
                value={locationFilter}
                onChange={(event) => setLocationFilter(event.target.value)}
              >
                <option value="">Any store</option>
                {storeOptions.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>

              {locationFilter && (
                <Button variant="ghost" size="sm" onClick={() => setLocationFilter('')}>
                  Clear
                </Button>
              )}
            </div>
          }
        />
      </Card>

      {dialog && (
        <TerminalFormDialog open terminal={dialog.terminal} onClose={() => setDialog(null)} />
      )}
    </div>
  )
}
